import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User, FileText, AlertCircle, Calendar, Clock, Users, UserCog, ClipboardList, RefreshCw, Loader2, Play, CheckCircle, Eye, Send, Upload, XCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PersonalProfilePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('requests');
  const [requestsSubTab, setRequestsSubTab] = useState('hr');
  const [tasksSubTab, setTasksSubTab] = useState('recurring');
  
  // Tasks state
  const [recurringTasks, setRecurringTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Task Form Modal
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [currentInstance, setCurrentInstance] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [taskNotes, setTaskNotes] = useState('');
  const [hrNotes, setHrNotes] = useState('');
  const [signatureFiles, setSignatureFiles] = useState([]);

  useEffect(() => {
    if (activeTab === 'tasks') {
      loadTasks();
    }
  }, [activeTab]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my-tasks/recurring`);
      setRecurringTasks(res.data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
    setLoading(false);
  };

  const handleStartTask = async (task) => {
    try {
      // Start the task (create instance for today)
      const res = await axios.post(`${API}/my-tasks/${task.id}/start`);
      const instanceId = res.data.instance_id;
      
      // Get instance details
      const instanceRes = await axios.get(`${API}/task-instances/${instanceId}`);
      setCurrentTask(task);
      setCurrentInstance(instanceRes.data);
      
      // Initialize attendance records
      const records = {};
      if (instanceRes.data.employees_by_company) {
        Object.entries(instanceRes.data.employees_by_company).forEach(([companyId, company]) => {
          company.employees.forEach(emp => {
            // Check if there's existing record
            const existing = instanceRes.data.attendance_records?.find(r => r.employee_id === emp.id);
            records[emp.id] = {
              employee_id: emp.id,
              employee_name: emp.name,
              company_id: companyId,
              company_name: company.name,
              status: existing?.status || 'present',
              notes: existing?.notes || ''
            };
          });
        });
      }
      setAttendanceRecords(records);
      setTaskNotes(instanceRes.data.notes || '');
      setHrNotes(instanceRes.data.hr_notes || '');
      setSignatureFiles(instanceRes.data.signature_files || []);
      setTaskModalOpen(true);
      
      toast.success('Görev başlatıldı');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : (Array.isArray(error.response?.data?.detail) ? error.response.data.detail.map(e => e.msg).join(', ') : 'Görev başlatılamadı'));
    }
  };

  const handleViewTask = async (task) => {
    if (!task.today_instance) {
      toast.error('Bugün için görev örneği bulunamadı');
      return;
    }
    
    try {
      const instanceRes = await axios.get(`${API}/task-instances/${task.today_instance.id}`);
      setCurrentTask(task);
      setCurrentInstance(instanceRes.data);
      
      // Initialize attendance records from existing data
      const records = {};
      if (instanceRes.data.employees_by_company) {
        Object.entries(instanceRes.data.employees_by_company).forEach(([companyId, company]) => {
          company.employees.forEach(emp => {
            const existing = instanceRes.data.attendance_records?.find(r => r.employee_id === emp.id);
            records[emp.id] = {
              employee_id: emp.id,
              employee_name: emp.name,
              company_id: companyId,
              company_name: company.name,
              status: existing?.status || 'present',
              notes: existing?.notes || ''
            };
          });
        });
      }
      setAttendanceRecords(records);
      setTaskNotes(instanceRes.data.notes || '');
      setHrNotes(instanceRes.data.hr_notes || '');
      setSignatureFiles(instanceRes.data.signature_files || []);
      setTaskModalOpen(true);
    } catch (error) {
      toast.error('Görev detayları yüklenemedi');
    }
  };

  const updateAttendanceStatus = (empId, status) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [empId]: { ...prev[empId], status }
    }));
  };

  const updateAttendanceNotes = (empId, notes) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [empId]: { ...prev[empId], notes }
    }));
  };

  const handleSaveTask = async () => {
    if (!currentInstance) return;
    
    try {
      const recordsArray = Object.values(attendanceRecords);
      
      await axios.put(`${API}/task-instances/${currentInstance.id}`, null, {
        params: {
          attendance_records: JSON.stringify(recordsArray),
          signature_files: signatureFiles.join(','),
          notes: taskNotes,
          hr_notes: hrNotes
        }
      });
      
      toast.success('Görev kaydedildi');
      loadTasks();
    } catch (error) {
      toast.error('Görev kaydedilemedi');
    }
  };

  const handleSubmitTask = async () => {
    if (!currentInstance) return;
    
    // Save first
    await handleSaveTask();
    
    try {
      await axios.post(`${API}/task-instances/${currentInstance.id}/submit`);
      toast.success('Görev tamamlandı ve onaya gönderildi');
      setTaskModalOpen(false);
      loadTasks();
    } catch (error) {
      toast.error('Görev gönderilemedi');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Bekliyor', color: 'bg-amber-100 text-amber-700' },
      in_progress: { label: 'Devam Ediyor', color: 'bg-blue-100 text-blue-700' },
      submitted: { label: 'Onay Bekliyor', color: 'bg-purple-100 text-purple-700' },
      approved: { label: 'Onaylandı', color: 'bg-green-100 text-green-700' },
      rejected: { label: 'Geri Gönderildi', color: 'bg-red-100 text-red-700' },
      sent_to_hr: { label: 'İK\'ya İletildi', color: 'bg-teal-100 text-teal-700' }
    };
    const c = config[status] || config.pending;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const groupEmployeesByCompany = () => {
    const groups = {};
    Object.values(attendanceRecords).forEach(rec => {
      if (!groups[rec.company_id]) {
        groups[rec.company_id] = {
          name: rec.company_name,
          employees: []
        };
      }
      groups[rec.company_id].employees.push(rec);
    });
    return groups;
  };

  return (
    <div className="space-y-6" data-testid="personal-profile-page">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Profilim</h2>
        <p className="text-[hsl(var(--muted-foreground))]">Talepleriniz, izinleriniz ve mesai bilgileriniz</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="requests" data-testid="tab-requests">
            <FileText className="h-4 w-4 mr-2" />
            Taleplerim
          </TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">
            <Calendar className="h-4 w-4 mr-2" />
            İzinlerim
          </TabsTrigger>
          <TabsTrigger value="warnings" data-testid="tab-warnings">
            <AlertCircle className="h-4 w-4 mr-2" />
            Uyarılar-Savunma
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">
            <Clock className="h-4 w-4 mr-2" />
            Mesai Çizelgem
          </TabsTrigger>
        </TabsList>

        {/* Taleplerim Tab */}
        <TabsContent value="requests" className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={requestsSubTab === 'hr' ? 'default' : 'outline'}
              onClick={() => setRequestsSubTab('hr')}
              className={requestsSubTab === 'hr' ? 'bg-teal-600 hover:bg-teal-700' : ''}
              data-testid="requests-subtab-hr"
            >
              <Users className="h-4 w-4 mr-2" />
              İnsan Kaynaklarından Talepler
            </Button>
            <Button
              variant={requestsSubTab === 'manager' ? 'default' : 'outline'}
              onClick={() => setRequestsSubTab('manager')}
              className={requestsSubTab === 'manager' ? 'bg-orange-600 hover:bg-orange-700' : ''}
              data-testid="requests-subtab-manager"
            >
              <UserCog className="h-4 w-4 mr-2" />
              Yöneticiden Talepler
            </Button>
          </div>

          {requestsSubTab === 'hr' && (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="h-16 w-16 mx-auto mb-4 text-teal-200" />
                <h3 className="text-xl font-bold mb-2">İnsan Kaynaklarından Talepler</h3>
                <p className="text-gray-500">İK biriminden gelen talepler burada görüntülenecek</p>
              </CardContent>
            </Card>
          )}

          {requestsSubTab === 'manager' && (
            <Card>
              <CardContent className="p-8 text-center">
                <UserCog className="h-16 w-16 mx-auto mb-4 text-orange-200" />
                <h3 className="text-xl font-bold mb-2">Yöneticiden Talepler</h3>
                <p className="text-gray-500">Yöneticinizden gelen talepler burada görüntülenecek</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Other Tabs (İzinlerim, Uyarılar, Mesai) */}
        <TabsContent value="leave" className="space-y-4">
          <Card>
            <CardContent className="p-8 text-center">
              <Calendar className="h-16 w-16 mx-auto mb-4 text-green-200" />
              <h3 className="text-xl font-bold mb-2">İzinlerim</h3>
              <p className="text-gray-500">İzin bakiyenizi ve geçmiş izinlerinizi görüntüleyin</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="warnings" className="space-y-4">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-200" />
              <h3 className="text-xl font-bold mb-2">Uyarılar ve Savunma</h3>
              <p className="text-gray-500">Size verilen uyarıları görüntüleyin</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardContent className="p-8 text-center">
              <Clock className="h-16 w-16 mx-auto mb-4 text-blue-200" />
              <h3 className="text-xl font-bold mb-2">Mesai Çizelgem</h3>
              <p className="text-gray-500">Vardiya programınızı görüntüleyin</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Task Form Modal */}
      <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Günlük Mesai Takibi - {currentTask?.title}</DialogTitle>
          </DialogHeader>
          
          {currentInstance && (
            <div className="space-y-6">
              {/* Task Info */}
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Tarih</p>
                    <p className="font-medium">{currentInstance.date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Durum</p>
                    {getStatusBadge(currentInstance.status)}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Şirketler</p>
                    <p className="font-medium">
                      {Object.values(currentInstance.employees_by_company || {}).map(c => c.name).join(', ')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Attendance Records by Company */}
              {Object.entries(groupEmployeesByCompany()).map(([companyId, company]) => (
                <Card key={companyId}>
                  <CardHeader>
                    <CardTitle className="text-lg">{company.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Çalışan</TableHead>
                          <TableHead>Durum</TableHead>
                          <TableHead>Not</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {company.employees.map(emp => (
                          <TableRow key={emp.employee_id}>
                            <TableCell className="font-medium">{emp.employee_name}</TableCell>
                            <TableCell>
                              <Select
                                value={emp.status}
                                onValueChange={(val) => updateAttendanceStatus(emp.employee_id, val)}
                                disabled={currentInstance.status !== 'in_progress' && currentInstance.status !== 'rejected'}
                              >
                                <SelectTrigger className="w-[130px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="present">Geldi</SelectItem>
                                  <SelectItem value="absent">Gelmedi</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                value={emp.notes}
                                onChange={(e) => updateAttendanceNotes(emp.employee_id, e.target.value)}
                                placeholder="Not ekle..."
                                disabled={currentInstance.status !== 'in_progress' && currentInstance.status !== 'rejected'}
                                className="w-full"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}

              {/* Signature Files */}
              <div>
                <Label className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  İmza Kağıtları (Dosya URL'leri)
                </Label>
                <Textarea
                  value={signatureFiles.join('\n')}
                  onChange={(e) => setSignatureFiles(e.target.value.split('\n').filter(s => s.trim()))}
                  placeholder="Her satıra bir dosya URL'si girin..."
                  rows={2}
                  disabled={currentInstance.status !== 'in_progress' && currentInstance.status !== 'rejected'}
                />
                <p className="text-xs text-gray-500 mt-1">Dosyaları yükledikten sonra URL'lerini buraya yapıştırın</p>
              </div>

              {/* Notes */}
              <div>
                <Label>Notlar</Label>
                <Textarea
                  value={taskNotes}
                  onChange={(e) => setTaskNotes(e.target.value)}
                  placeholder="Ek notlar..."
                  rows={2}
                  disabled={currentInstance.status !== 'in_progress' && currentInstance.status !== 'rejected'}
                />
              </div>

              {/* HR Notes */}
              <div>
                <Label>İK İçin Açıklama</Label>
                <Textarea
                  value={hrNotes}
                  onChange={(e) => setHrNotes(e.target.value)}
                  placeholder="İK personeline iletilecek notlar..."
                  rows={2}
                  disabled={currentInstance.status !== 'in_progress' && currentInstance.status !== 'rejected'}
                />
              </div>

              {/* Actions */}
              {(currentInstance.status === 'in_progress' || currentInstance.status === 'rejected') && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button onClick={handleSaveTask} variant="outline" className="flex-1">
                    Kaydet
                  </Button>
                  <Button onClick={handleSubmitTask} className="flex-1 bg-green-600 hover:bg-green-700 gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Görevi Tamamla
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonalProfilePage;
